import type { StockArticle } from "@/lib/stock.api";
import type { StockMovementWithArticle } from "@/hooks/queries/useStockMovements";

export interface StockMetrics {
  activeCount: number;
  stockUnits: number;
  stockValue: number;
  potentialGain: number;
  lowStockCount: number;
  outOfStockCount: number;
  negativeOverrides: number;
}

export function getSupplierPrice(article: StockArticle): number {
  return parseFloat(article.defaultSupplierPrice);
}

export function getSellingPrice(article: StockArticle): number {
  return parseFloat(article.defaultSellingPrice);
}

export function getUnitMargin(article: StockArticle): number {
  return getSellingPrice(article) - getSupplierPrice(article);
}

export function getPositiveOnHand(article: StockArticle): number {
  return Math.max(article.onHand, 0);
}

export function getArticleStockValue(article: StockArticle): number {
  return getPositiveOnHand(article) * getSupplierPrice(article);
}

export function getArticlePotentialGain(article: StockArticle): number {
  return getPositiveOnHand(article) * getUnitMargin(article);
}

export function isLowStock(article: StockArticle): boolean {
  return article.active && article.onHand < article.minLevel;
}

export function getStockMetrics(
  articles: StockArticle[],
  movements: StockMovementWithArticle[],
): StockMetrics {
  const lowStock = articles.filter(isLowStock);

  return {
    activeCount: articles.filter((article) => article.active).length,
    stockUnits: articles.reduce((sum, article) => sum + getPositiveOnHand(article), 0),
    stockValue: articles.reduce((sum, article) => sum + getArticleStockValue(article), 0),
    potentialGain: articles.reduce((sum, article) => sum + getArticlePotentialGain(article), 0),
    lowStockCount: lowStock.length,
    outOfStockCount: articles.filter((article) => article.active && article.onHand <= 0).length,
    negativeOverrides: movements.filter((movement) => movement.isNegativeOverride).length,
  };
}
